import { useTranslation } from "react-i18next";
import { AiOutlineMessage } from "react-icons/ai";
import { MdOutlineFeedback } from "react-icons/md";

import type {
  FeedbackFormModel,
  FeedbackWidgetConfig,
} from "design-system/components/FeedbackWidget/types";

import { FeedbackWidget } from "design-system/components/FeedbackWidget";
import { withTracking } from "sandbox/analytics/amplitude";
import { useSubmitFeedback } from "sandbox/api/hooks/useSandboxMutations";
import { useThemeMode } from "sandbox/contexts/ThemeModeContext";

import { fromFeedback } from "./serializers";

export const SandboxFeedbackWidget = () => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { mode } = useThemeMode();

  const { mutateAsync: submitFeedback } = useSubmitFeedback();

  const config = {
    launcher: {
      label: t("common:components.feedbackWidget.launcher.label"),
      ariaLabel: t("common:components.feedbackWidget.launcher.label"),
      icon: <MdOutlineFeedback />,
    },
    modal: {
      title: t("common:components.feedbackWidget.modal.title"),
      headerIcon: (
        <AiOutlineMessage
          style={mode === "dark" ? { color: "#ffffff" } : undefined}
        />
      ),
      closeButtonAriaLabel: t(
        "common:components.feedbackWidget.modal.closeButtonAriaLabel",
      ),
    },
    steps: [
      {
        type: "sentiment",
        question: t("sandbox:components.feedbackWidget.sentiment.question"),
        options: [
          {
            value: "needs_work",
            label: (
              <span style={mode === "dark" ? { color: "#ffffff" } : undefined}>
                {t("common:components.feedbackWidget.sentiment.needsWork")}
              </span>
            ),
            emoji: "🙁",
          },
          {
            value: "okay",
            label: (
              <span style={mode === "dark" ? { color: "#ffffff" } : undefined}>
                {t("common:components.feedbackWidget.sentiment.okay")}
              </span>
            ),
            emoji: "😐",
          },
          {
            value: "satisfied",
            label: (
              <span style={mode === "dark" ? { color: "#ffffff" } : undefined}>
                {t("common:components.feedbackWidget.sentiment.satisfied")}
              </span>
            ),
            emoji: "😄",
          },
        ],
      },
      {
        type: "rating",
        prompt: t("common:components.feedbackWidget.rating.prompt"),
        revealAfter: 0,
        rows: [
          {
            key: "easeOfGettingStarted",
            label: t(
              "sandbox:components.feedbackWidget.rating.easeOfGettingStarted",
            ),
          },
          {
            key: "apiExplorer",
            label: t("sandbox:components.sandboxSidebar.labels.sandbox"),
          },
          {
            key: "documentation",
            label: t("common:components.userMenu.documentationPortal"),
          },
        ],
      },
      {
        type: "comment",
        revealAfter: 0,
        placeholder: t("sandbox:components.feedbackWidget.comment.placeholder"),
        labelsByOption: {
          needs_work: t(
            "common:components.feedbackWidget.comment.needsWorkLabel",
          ),
          okay: t("common:components.feedbackWidget.comment.okayLabel"),
          satisfied: t(
            "common:components.feedbackWidget.comment.satisfiedLabel",
          ),
        },
        optionalSuffix: t("common:components.feedbackWidget.comment.optional"),
        maxLength: 1000,
      },
    ],
    submit: { label: t("common:components.feedbackWidget.submit.label") },
    toast: { message: t("common:components.feedbackWidget.toast.message") },
    error: {
      submitFailed: t("common:components.feedbackWidget.error.submitFailed"),
    },
  } as FeedbackWidgetConfig;

  const onSubmit = async (payload: unknown) => {
    await withTracking("Feedback Submitted", () =>
      submitFeedback(fromFeedback(payload as FeedbackFormModel)),
    );
  };

  return <FeedbackWidget config={config} onSubmit={onSubmit} />;
};
